import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { X, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Research() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [buying, setBuying] = useState(null);
  const [purchasedIds, setPurchasedIds] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [requestText, setRequestText] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/research`)
      .then(res => setArticles(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    if (user) {
      axios.get(`${API}/user/purchased-research`)
        .then(res => setPurchasedIds(res.data.purchased_research || []))
        .catch(() => {});
    }
  }, [user]);

  const handleProductRequest = async () => {
    if (!requestText.trim()) return;
    setSubmittingRequest(true);
    try {
      await axios.post(`${API}/research/request`, { message: requestText.trim() });
      toast.success('Your request has been submitted!');
      setRequestText('');
      setRequestSubmitted(true);
      setTimeout(() => setRequestSubmitted(false), 5000);
    } catch {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmittingRequest(false);
    }
  };


  const handleBuy = async (article) => {
    if (!user) {
      toast.error('Please login to purchase');
      navigate('/login');
      return;
    }

    setBuying(article.id);
    try {
      const response = await axios.post(`${API}/checkout/research`, { product_id: article.id });
      const { order_id, amount, key_id, user_name, user_email, package_label } = response.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: 'INR',
        name: 'The Case Verse',
        description: package_label,
        order_id: order_id,
        prefill: { name: user_name, email: user_email },
        theme: { color: '#0f172a' },
        handler: async function (razorpayResponse) {
          try {
            await axios.post(`${API}/checkout/verify`, {
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature
            });
            toast.success('Payment successful! You now have access to this product.');
            setPurchasedIds(prev => [...prev, article.id]);
            setShowSuccessPopup(true);
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          } finally {
            setBuying(null);
          }
        },
        modal: {
          ondismiss: function () {
            setBuying(null);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        toast.error('Payment failed: ' + (response.error?.description || 'Unknown error'));
        setBuying(null);
      });
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setBuying(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="research-page">
      <Navbar />
      <div className="pt-24 pb-12 px-6">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-5xl font-serif font-bold tracking-tight text-slate-900 mb-4">Research Board</h1>
          <p className="text-lg text-slate-600 mb-10">Explore our publications and case study collections</p>

          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>
          ) : articles.length === 0 ? (
            <div className="text-center py-12 bg-white rounded border border-slate-200">
              <p className="text-slate-600">No products available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="research-table">
              <table className="w-full">
                <thead className="bg-slate-800 text-white">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold w-16">S.No</th>
                    <th className="text-left p-4 text-sm font-semibold">Product Title</th>
                    <th className="text-left p-4 text-sm font-semibold w-40">Product Type</th>
                    <th className="text-left p-4 text-sm font-semibold w-48">Price (In INR)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {articles.map((article, index) => (
                    <tr key={article.id} className="hover:bg-slate-50" data-testid={`research-row-${article.id}`}>
                      <td className="p-4 text-sm text-slate-700 align-top">{index + 1}</td>
                      <td className="p-4 align-top">
                        <div className="text-slate-900 font-medium mb-2">{article.title}</div>
                        <button
                          onClick={() => setSelectedArticle(article)}
                          className="text-sky-600 font-semibold hover:text-sky-800 hover:underline transition-colors text-sm"
                          data-testid={`know-more-${article.id}`}
                        >
                          Know More
                        </button>
                      </td>
                      <td className="p-4 text-sm text-slate-700 align-top">{article.product_type || '-'}</td>
                      <td className="p-4 align-top">
                        <div className="text-slate-900 font-semibold mb-3">
                          {article.price ? `Rs ${article.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'}
                        </div>
                        {article.price > 0 && (
                          purchasedIds.includes(article.id) ? (
                            <span className="inline-block px-5 py-1.5 bg-green-100 text-green-700 font-bold rounded text-sm" data-testid={`purchased-badge-${article.id}`}>
                              PURCHASED
                            </span>
                          ) : (
                          <button
                            onClick={() => handleBuy(article)}
                            disabled={buying === article.id}
                            className="px-5 py-1.5 bg-sky-500 text-white font-bold rounded hover:bg-sky-600 transition-colors text-sm disabled:opacity-50"
                            data-testid={`buy-btn-${article.id}`}
                          >
                            {buying === article.id ? 'Processing...' : 'BUY'}
                          </button>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Product Request Box */}
          {user && (
            <div className="mt-10 bg-white rounded-xl border border-slate-200 p-6" data-testid="product-request-section">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Can't find what you're looking for?</h3>
              <p className="text-sm text-slate-600 mb-4">Describe the product you need and our team will look into it.</p>
              <div className="flex gap-3">
                <textarea
                  value={requestText}
                  onChange={(e) => setRequestText(e.target.value)}
                  placeholder="Describe the product you're looking for..."
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none text-sm"
                  rows={3}
                  data-testid="product-request-input"
                />
                <div className="flex flex-col justify-end">
                  <button
                    onClick={handleProductRequest}
                    disabled={submittingRequest || !requestText.trim()}
                    className="px-6 py-3 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    data-testid="product-request-submit"
                  >
                    {submittingRequest ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </div>
              {requestSubmitted && (
                <p className="mt-3 text-sm text-green-600 font-medium" data-testid="request-success-msg">
                  Your request has been submitted successfully. Our team will review it shortly.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Know More Popup */}
      {selectedArticle && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" data-testid="know-more-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 relative">
            <button onClick={() => setSelectedArticle(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid="close-modal-btn">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-serif font-bold text-slate-900 mb-2" data-testid="modal-title">{selectedArticle.title}</h2>
            {selectedArticle.product_type && (
              <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full mb-3">{selectedArticle.product_type}</span>
            )}
            {selectedArticle.price > 0 && (
              <p className="text-lg font-semibold text-slate-900 mb-4">Rs {selectedArticle.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            )}
            <div className="prose prose-slate max-w-none mb-6" data-testid="modal-content" dangerouslySetInnerHTML={{ __html: selectedArticle.content }} />
            {selectedArticle.price > 0 && (
              purchasedIds.includes(selectedArticle.id) ? (
                <div className="w-full py-3 bg-green-100 text-green-700 font-bold rounded-lg text-center" data-testid="modal-purchased-badge">
                  PURCHASED
                </div>
              ) : (
              <button
                onClick={() => { setSelectedArticle(null); handleBuy(selectedArticle); }}
                className="w-full py-3 bg-sky-500 text-white font-bold rounded-lg hover:bg-sky-600 transition-colors"
                data-testid="modal-buy-btn"
              >
                BUY — Rs {selectedArticle.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </button>
              )
            )}
          </div>
        </div>
      )}
      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-6" data-testid="purchase-success-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-10 text-center relative shadow-2xl">
            <button
              onClick={() => setShowSuccessPopup(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"
              data-testid="close-success-popup"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>
            <h2 className="text-2xl font-serif font-bold text-slate-900 mb-3" data-testid="success-popup-title">
              Congratulations!
            </h2>
            <p className="text-lg text-slate-700 mb-8" data-testid="success-popup-message">
              Your Purchase is Successful. Your Product will arrive soon
            </p>
            <button
              onClick={() => setShowSuccessPopup(false)}
              className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
              data-testid="success-popup-ok-btn"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
